import { businessData as businessDataAtom } from 'atoms';
import OfficeStaffTable from 'pages/Business/OfficeStaff/OfficeStaffTable';
import InnerPageLayout from 'pages/Business/Partials/InnerPageLayout';
import { useRecoilState } from 'recoil';
import styled from 'styled-components';

import { toasterNotification } from 'utils/toaster';
import { useAuth } from 'context/AuthContext';

import React, { useEffect, useState } from 'react';

import EmptyOfficeStaff from './EmptyOfficeStaff';
import StaffMemberPopup from './StaffMemberPopup';

const DataContainer = styled.div`
    background: ${(props) => props.theme.colorBackground};
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    flexgrow: 1;
`;

const OfficeStaffHome = () => {
    const { token, loading: authLoading } = useAuth();
    const [officeStaffList, setOfficeStaffList] = useState([]);
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    const [rows, setRows] = useState([]);
    const [openPopup, setOpenPopup] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    const getOfficeStaffList = async () => {
        setIsLoading(true);
        
        try {
            // Validate business_id
            if (!business?.id) {
                console.error('Business ID is required');
                setOfficeStaffList([]);
                setIsLoading(false);
                return;
            }

            // Validate access token (only show error if auth is loaded and token is still missing)
            if (authLoading) {
                // Still loading, wait for auth to complete
                setOfficeStaffList([]);
                setIsLoading(false);
                return;
            }
            
            if (!token) {
                console.error('No authentication token found');
                toasterNotification('error', '', 'Authentication error. Please try again.');
                setOfficeStaffList([]);
                setIsLoading(false);
                return;
            }

            // Fetch office staff from backend API
            const response = await fetch(
                `${process.env.REACT_APP_SERVER_URL}/api/providers/office-staff`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ business_id: business.id })
                }
            );

            const result = await response.json();

            if (!response.ok || result.error) {
                const errorMessage = result.error || 'Failed to fetch office staff';
                console.error('Error fetching office staff:', errorMessage);
                toasterNotification('error', '', errorMessage);
                setOfficeStaffList([]);
                setIsLoading(false);
                return;
            }

            // Preserve object structure - do not convert to array
            // Set the office staff list from API response
            const officeStaffData = result.data;
            setOfficeStaffList(Array.isArray(officeStaffData) ? officeStaffData : (officeStaffData ? [officeStaffData] : []));
        } catch (error) {
            console.error('Error in getOfficeStaffList:', error);
            toasterNotification('error', '', 'An unexpected error occurred. Please try again.');
            setOfficeStaffList([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (business?.id) {
            getOfficeStaffList().then();
        }
    }, [business?.id]);

    useEffect(() => {
        if (officeStaffList?.length > 0) {
            let rows = officeStaffList.map((officeStaff) => {
                return {
                    id: officeStaff.id,
                    first_name: officeStaff?.first_name,
                    last_name: officeStaff.last_name || '',
                    email: officeStaff.email || '',
                    role_id: officeStaff.role_id || '',
                    business_id: officeStaff.business_id || '',
                    name: officeStaff?.first_name + ' ' + officeStaff.last_name,
                    role: officeStaff.role_id == 1 ? 'Admin' : 'General',
                };
            });
            setRows(rows);
        } else {
            setRows([]);
        }
    }, [officeStaffList]);

    const togglePopup = () => {
        setOpenPopup(!openPopup);
    };

    return (
        <>
            {!isLoading && officeStaffList?.length === 0 && (
                <div style={{ display: 'flex', flexDirection: 'column', flex: 1, height: '100%' }}>
                    <EmptyOfficeStaff togglePopup={togglePopup}></EmptyOfficeStaff>
                </div>
            )}

            {!isLoading && officeStaffList?.length > 0 && (
                <DataContainer>
                    <InnerPageLayout togglePopup={togglePopup} heading="Office Staff" btnText="Add a Staff Member" buttonType="secondary">
                        <OfficeStaffTable rows={rows} getOfficeStaffList={getOfficeStaffList}></OfficeStaffTable>
                    </InnerPageLayout>
                </DataContainer>
            )}

            <StaffMemberPopup
                open={openPopup}
                onClose={togglePopup}
                staffData={''}
                getOfficeStaffList={getOfficeStaffList}
                businessId={business?.id}
            ></StaffMemberPopup>
        </>
    );
};

export default OfficeStaffHome;
